import React, { useState, useRef } from 'react';
import { Holding, LiquidityEvent, Loan, MacroShock, Settings, Currency, HoldingType } from '../types';
import { Card, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { COUNTRIES, CURRENCIES, getDefaultCurrency } from '../utils/countries';
import { Shield, Sparkles, User, ArrowRight, Wallet, Globe, CheckCircle2, Upload, FileText, AlertCircle, Users, Briefcase, CreditCard, TrendingUp } from 'lucide-react';
export interface OnboardingData {
  partnerNames: [string, string];
  settings: Settings;
  holdings: Holding[];
  liquidityEvents: LiquidityEvent[];
  loans: Loan[];
  macroShocks?: MacroShock[];
  isDemo?: boolean;
}
interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void;
}
const generateId = () => Math.random().toString(36).substr(2, 9);
export function OnboardingWizard({
  onComplete
}: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  // Manual Setup State
  const [p1Name, setP1Name] = useState('');
  const [p1Dob, setP1Dob] = useState('');
  const [p2Name, setP2Name] = useState('');
  const [p2Dob, setP2Dob] = useState('');
  const [country, setCountry] = useState('US');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [assetName, setAssetName] = useState('');
  const [assetAmount, setAssetAmount] = useState('');
  const [assetType, setAssetType] = useState<HoldingType>('Liquid');
  // Import State
  const [importedData, setImportedData] = useState<OnboardingData | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const defaultCurr = getDefaultCurrency(newCountry);
    if (defaultCurr) {
      setCurrency(defaultCurr);
    }
  };
  const handleLoadDemo = () => {
    const now = new Date().toISOString();
    const demoSettings: Settings = {
      baseCurrency: 'EUR',
      baseCountry: 'BE',
      withdrawalStrategy: 'drawdown',
      partnerDOBs: ['1994-03-15', '1992-08-22'],
      drawdownReturnRate: 4,
      monthlyContribution: 2500,
      timeHorizon: 30,
      desiredDrawdown: 6000,
      inflationEnabled: true,
      inflationRate: 2.5
    };
    const demoHoldings: Holding[] = [{
      id: generateId(),
      person: 'Alex',
      name: 'KBC Checking Account',
      amount: 12000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 0.5,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 12000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Alex',
      name: 'IWDA World ETF (Bolero)',
      amount: 78000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 9,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 78000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Belfius Savings Account',
      amount: 22000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 2.5,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 22000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Revolut EUR Account',
      amount: 4800,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 1,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 4800,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Joint',
      name: 'Argenta Emergency Fund',
      amount: 18000,
      currency: 'EUR',
      type: 'Liquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 2,
      returnType: 'interest',
      capitalGainsTaxRate: 0,
      history: [{
        amount: 18000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Alex',
      name: 'Company RSUs',
      amount: 40000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 12,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      drawdownAvailableYear: 2,
      yieldsAfterDrawdown: false,
      history: [{
        amount: 40000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Bitcoin',
      amount: 10000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 0,
      returnType: 'none',
      capitalGainsTaxRate: 33,
      drawdownAvailableYear: 0,
      yieldsAfterDrawdown: false,
      history: [{
        amount: 10000,
        date: now
      }]
    }, {
      id: generateId(),
      person: 'Jordan',
      name: 'Pensioensparen (KBC)',
      amount: 55000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 7,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 8,
      drawdownAvailableYear: 25,
      yieldsAfterDrawdown: true,
      history: [{
        amount: 55000,
        date: now
      }]
    }, {
      id: 'demo-re-1',
      person: 'Joint',
      name: 'Brussels Rental Apartment',
      amount: 320000,
      currency: 'EUR',
      type: 'Illiquid',
      country: 'BE',
      createdAt: now,
      isActive: true,
      expectedReturn: 2.5,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      isRealEstate: true,
      purchasePrice: 280000,
      appreciationRate: 2.5,
      rentalIncome: 1400,
      linkedLoanId: 'demo-loan-1',
      linkedEventIds: ['demo-event-1'],
      autoCreatedEventIds: ['demo-sale-event-1'],
      realEstateStrategy: 'sell',
      plannedSaleYear: 15,
      drawdownAvailableYear: 15,
      yieldsAfterDrawdown: false,
      history: [{
        amount: 320000,
        date: now
      }]
    }];
    const demoLoans: Loan[] = [{
      id: 'demo-loan-1',
      name: 'Brussels Apartment Mortgage',
      person: 'Joint',
      totalAmount: 224000,
      depositAmount: 56000,
      interestRate: 3.5,
      durationMonths: 300,
      startYear: 0,
      currency: 'EUR',
      repaymentType: 'annuity',
      isActive: true,
      createdAt: now
    }];
    const demoEvents: LiquidityEvent[] = [{
      id: 'demo-event-1',
      name: 'Brussels Apartment Rental Income',
      amount: 1400,
      currency: 'EUR',
      type: 'monthly',
      startYear: 0,
      endYear: 15,
      isActive: true
    }, {
      id: 'demo-sale-event-1',
      name: 'Brussels Rental Apartment Sale Proceeds',
      amount: Math.round(320000 * Math.pow(1.025, 15)),
      currency: 'EUR',
      type: 'one-time',
      startYear: 15,
      isActive: true,
      displayOnly: true,
      displayOnlyMeta: {
        sourceHolding: 'Brussels Rental Apartment',
        currentValue: 320000,
        appreciationRate: 2.5,
        mechanism: "Processed by the projection engine's illiquid bucket system. The property appreciates monthly until the sale year, then its full value enters the drawdown pool as non-yielding cash."
      }
    }, {
      id: generateId(),
      name: 'Alex Year-End Bonus',
      amount: 6000,
      currency: 'EUR',
      type: 'yearly',
      startYear: 0,
      isActive: true
    }, {
      id: generateId(),
      name: 'Jordan Freelance Consulting',
      amount: 450,
      currency: 'EUR',
      type: 'monthly',
      startYear: 0,
      endYear: 5,
      isActive: true
    }];
    const demoShocks: MacroShock[] = [{
      id: generateId(),
      name: 'Market Correction',
      year: 5,
      liquidImpact: -30,
      illiquidImpact: -15,
      realEstateImpact: -10,
      isActive: true
    }];
    onComplete({
      partnerNames: ['Alex', 'Jordan'],
      settings: demoSettings,
      holdings: demoHoldings,
      liquidityEvents: demoEvents,
      loans: demoLoans,
      macroShocks: demoShocks,
      isDemo: true
    });
  };
  const handleFinishManual = () => {
    const now = new Date().toISOString();
    const manualSettings: Settings = {
      baseCurrency: currency,
      baseCountry: country,
      withdrawalStrategy: 'drawdown',
      partnerDOBs: [p1Dob, p2Dob],
      drawdownReturnRate: 4,
      monthlyContribution: 0,
      timeHorizon: 30,
      desiredDrawdown: 5000,
      inflationEnabled: false,
      inflationRate: 3
    };
    const manualHoldings: Holding[] = [{
      id: generateId(),
      person: p1Name,
      name: assetName,
      amount: Number(assetAmount),
      currency: currency,
      type: assetType,
      country: country,
      createdAt: now,
      isActive: true,
      expectedReturn: 0,
      returnType: 'capital-growth',
      capitalGainsTaxRate: 0,
      history: [{
        amount: Number(assetAmount),
        date: now
      }]
    }];
    onComplete({
      partnerNames: [p1Name, p2Name],
      settings: manualSettings,
      holdings: manualHoldings,
      liquidityEvents: [],
      loans: []
    });
  };
  const isStep1Valid = p1Name.trim() !== '' && p2Name.trim() !== '' && p1Dob !== '' && p2Dob !== '';
  const isStep3Valid = assetName.trim() !== '' && assetAmount !== '' && !isNaN(Number(assetAmount));
  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (!data.holdings || !Array.isArray(data.holdings)) {
          throw new Error('Invalid file: missing holdings data');
        }
        if (!data.settings || typeof data.settings !== 'object') {
          throw new Error('Invalid file: missing settings');
        }
        if (!data.partnerNames || !Array.isArray(data.partnerNames)) {
          throw new Error('Invalid file: missing partner names');
        }
        const imported: OnboardingData = {
          partnerNames: data.partnerNames as [string, string],
          settings: data.settings,
          holdings: data.holdings,
          liquidityEvents: data.liquidityEvents || [],
          loans: data.loans || []
        };
        setImportedData(imported);
        setStep(4);
      } catch (err: any) {
        setImportError(err.message || 'Could not read this file. Please use a valid Pairfolio export.');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleConfirmImport = () => {
    if (importedData) {
      onComplete(importedData);
    }
  };
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-id="element-1263">
      <div className="max-w-3xl w-full" data-id="element-1264">
        {/* Header / Privacy Banner */}
        <div className="text-center mb-8" data-id="element-1265">
          <img src="https://cdn.magicpatterns.com/uploads/sfi3oXipchL5LQrtC4MqUT/pairfolio_logo_2.png" alt="Pairfolio" className="w-16 h-16 object-contain mb-6 mx-auto" data-id="element-1266" />
          <h1 className="text-3xl font-bold text-slate-900 mb-3" data-id="element-1267">
            Welcome to Pairfolio
          </h1>
          <p className="text-slate-500 text-lg max-w-lg mx-auto" data-id="element-1268">
            Plan your financial future together. Let's get your portfolio set
            up.
          </p>

          <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-100" data-id="element-1269">
            <Shield className="w-4 h-4" data-id="element-1270" />
            Privacy First: All data is stored locally in your browser. Nothing
            is sent to our servers.
          </div>
        </div>

        {step === 0 && <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1271">
            <Card className="hover:border-blue-300 transition-all cursor-pointer group" onClick={handleLoadDemo} data-id="element-1272">
              <CardContent className="p-6 text-center h-full flex flex-col items-center justify-center" data-id="element-1273">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-1274">
                  <Sparkles className="w-6 h-6" data-id="element-1275" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-1276">
                  Explore Demo Data
                </h3>
                <p className="text-slate-500 text-sm mb-5" data-id="element-1277">
                  Populate the app with a realistic portfolio for a couple (Alex
                  & Jordan) to see how the projections and FIRE calculations
                  work.
                </p>
                <Button variant="primary" className="w-full mt-auto" onClick={e => {
              e.stopPropagation();
              handleLoadDemo();
            }} data-id="element-1278">
                  Load Demo Portfolio
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-slate-400 transition-all cursor-pointer group" onClick={() => setStep(1)} data-id="element-1279">
              <CardContent className="p-6 text-center h-full flex flex-col items-center justify-center" data-id="element-1280">
                <div className="w-12 h-12 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-1281">
                  <User className="w-6 h-6" data-id="element-1282" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-1283">
                  Start Fresh
                </h3>
                <p className="text-slate-500 text-sm mb-5" data-id="element-1284">
                  Set up your own profile manually. We'll guide you through
                  adding your names, base currency, and your first asset.
                </p>
                <Button variant="secondary" className="w-full mt-auto" onClick={e => {
              e.stopPropagation();
              setStep(1);
            }} data-id="element-1285">
                  Set Up Manually
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:border-emerald-300 transition-all cursor-pointer group" onClick={() => fileInputRef.current?.click()} data-id="element-1286">
              <CardContent className="p-6 text-center h-full flex flex-col items-center justify-center" data-id="element-1287">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-1288">
                  <Upload className="w-6 h-6" data-id="element-1289" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-1290">
                  Import from File
                </h3>
                <p className="text-slate-500 text-sm mb-5" data-id="element-1291">
                  Restore a previously exported Pairfolio file to pick up right
                  where you left off.
                </p>
                <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={handleFileImport} className="hidden" data-id="element-1292" />
                <Button variant="secondary" className="w-full mt-auto text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={e => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }} data-id="element-1293">
                  <Upload className="w-4 h-4 mr-2" data-id="element-1294" />
                  Choose File
                </Button>
              </CardContent>
            </Card>
          </div>}

        {/* Import error */}
        {step === 0 && importError && <div className="mt-4 flex items-start gap-2 p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200" data-id="element-1295">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1296" />
            {importError}
          </div>}

        {/* Import Preview (step 4) */}
        {step === 4 && importedData && <Card className="shadow-xl shadow-slate-200/50 border-slate-200" data-id="element-1297">
            <div className="px-8 pt-8 pb-4 border-b border-slate-100" data-id="element-1298">
              <div className="flex items-center gap-3 mb-2" data-id="element-1299">
                <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1300">
                  <FileText className="w-5 h-5 text-emerald-600" data-id="element-1301" />
                </div>
                <div data-id="element-1302">
                  <h2 className="text-xl font-bold text-slate-800" data-id="element-1303">
                    Import Preview
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-1304">
                    Review the data before importing
                  </p>
                </div>
              </div>
            </div>

            <CardContent className="p-8 space-y-6" data-id="element-1305">
              {/* Partners */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1306">
                <div className="flex items-center gap-2 mb-3" data-id="element-1307">
                  <Users className="w-4 h-4 text-indigo-500" data-id="element-1308" />
                  <h4 className="text-sm font-semibold text-slate-700" data-id="element-1309">
                    Partners
                  </h4>
                </div>
                <div className="flex items-center gap-3" data-id="element-1310">
                  <span className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-800" data-id="element-1311">
                    {importedData.partnerNames[0]}
                  </span>
                  <span className="text-slate-400" data-id="element-1312">&</span>
                  <span className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-800" data-id="element-1313">
                    {importedData.partnerNames[1]}
                  </span>
                </div>
                {(importedData.settings.partnerDOBs?.[0] || importedData.settings.partnerDOBs?.[1]) && <p className="text-xs text-slate-400 mt-2" data-id="element-1314">
                    DOBs: {importedData.settings.partnerDOBs[0] || '—'} ·{' '}
                    {importedData.settings.partnerDOBs[1] || '—'}
                  </p>}
              </div>

              {/* Holdings Summary */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1315">
                <div className="flex items-center gap-2 mb-3" data-id="element-1316">
                  <Briefcase className="w-4 h-4 text-blue-500" data-id="element-1317" />
                  <h4 className="text-sm font-semibold text-slate-700" data-id="element-1318">
                    Holdings
                  </h4>
                  <span className="ml-auto text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full" data-id="element-1319">
                    {importedData.holdings.length} total
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3" data-id="element-1320">
                  <div className="bg-white p-3 rounded-lg border border-slate-200 text-center" data-id="element-1321">
                    <p className="text-lg font-bold text-blue-700" data-id="element-1322">
                      {importedData.holdings.filter(h => h.type === 'Liquid').length}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1323">Liquid</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200 text-center" data-id="element-1324">
                    <p className="text-lg font-bold text-indigo-700" data-id="element-1325">
                      {importedData.holdings.filter(h => h.type === 'Illiquid' && !h.isRealEstate).length}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1326">Illiquid</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200 text-center" data-id="element-1327">
                    <p className="text-lg font-bold text-emerald-700" data-id="element-1328">
                      {importedData.holdings.filter(h => h.isRealEstate).length}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1329">Real Estate</p>
                  </div>
                </div>
                {importedData.holdings.length > 0 && <div className="mt-3 max-h-36 overflow-y-auto" data-id="element-1330">
                    <div className="space-y-1" data-id="element-1331">
                      {importedData.holdings.map(h => <div key={h.id} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-white border border-slate-100" data-id="element-1332">
                          <span className="text-slate-700 truncate mr-2" data-id="element-1333">
                            {h.name}
                          </span>
                          <span className="text-slate-500 shrink-0 font-medium tabular-nums" data-id="element-1334">
                            {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: h.currency || 'USD',
                      maximumFractionDigits: 0
                    }).format(h.amount)}
                          </span>
                        </div>)}
                    </div>
                  </div>}
              </div>

              {/* Events & Loans */}
              <div className="grid grid-cols-2 gap-4" data-id="element-1335">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1336">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1337">
                    <TrendingUp className="w-4 h-4 text-violet-500" data-id="element-1338" />
                    <h4 className="text-sm font-semibold text-slate-700" data-id="element-1339">
                      Cash Events
                    </h4>
                  </div>
                  <p className="text-2xl font-bold text-slate-800" data-id="element-1340">
                    {importedData.liquidityEvents.length}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-1341">
                    {importedData.liquidityEvents.filter(e => e.type === 'monthly').length}{' '}
                    monthly ·{' '}
                    {importedData.liquidityEvents.filter(e => e.type === 'yearly').length}{' '}
                    yearly ·{' '}
                    {importedData.liquidityEvents.filter(e => e.type === 'one-time').length}{' '}
                    one-time
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1342">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1343">
                    <CreditCard className="w-4 h-4 text-amber-500" data-id="element-1344" />
                    <h4 className="text-sm font-semibold text-slate-700" data-id="element-1345">
                      Loans
                    </h4>
                  </div>
                  <p className="text-2xl font-bold text-slate-800" data-id="element-1346">
                    {importedData.loans.length}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5" data-id="element-1347">
                    {importedData.loans.filter(l => l.isActive).length} active
                  </p>
                </div>
              </div>

              {/* Settings */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1348">
                <h4 className="text-sm font-semibold text-slate-700 mb-2" data-id="element-1349">
                  Settings
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-1350">
                  <span className="text-xs bg-white px-2.5 py-1 rounded-full border border-slate-200 text-slate-600" data-id="element-1351">
                    💰 {importedData.settings.baseCurrency}
                  </span>
                  <span className="text-xs bg-white px-2.5 py-1 rounded-full border border-slate-200 text-slate-600" data-id="element-1352">
                    📅 {importedData.settings.timeHorizon}yr horizon
                  </span>
                  <span className="text-xs bg-white px-2.5 py-1 rounded-full border border-slate-200 text-slate-600" data-id="element-1353">
                    💸{' '}
                    {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: importedData.settings.baseCurrency || 'USD',
                  maximumFractionDigits: 0
                }).format(importedData.settings.monthlyContribution)}
                    /mo contribution
                  </span>
                  <span className="text-xs bg-white px-2.5 py-1 rounded-full border border-slate-200 text-slate-600" data-id="element-1354">
                    🔥{' '}
                    {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: importedData.settings.baseCurrency || 'USD',
                  maximumFractionDigits: 0
                }).format(importedData.settings.desiredDrawdown)}
                    /mo drawdown
                  </span>
                  <span className="text-xs bg-white px-2.5 py-1 rounded-full border border-slate-200 text-slate-600" data-id="element-1355">
                    📈{' '}
                    {importedData.settings.withdrawalStrategy === 'interest-only' ? 'Interest Only' : 'Drawdown'}
                  </span>
                  {importedData.settings.inflationEnabled && <span className="text-xs bg-white px-2.5 py-1 rounded-full border border-slate-200 text-slate-600" data-id="element-1356">
                      📊 {importedData.settings.inflationRate}% inflation
                    </span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4" data-id="element-1357">
                <Button variant="ghost" onClick={() => {
              setStep(0);
              setImportedData(null);
            }} data-id="element-1358">
                  Back
                </Button>
                <Button variant="primary" onClick={handleConfirmImport} data-id="element-1359">
                  <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-1360" />
                  Import & Start
                </Button>
              </div>
            </CardContent>
          </Card>}

        {step > 0 && step <= 3 && <Card className="shadow-xl shadow-slate-200/50 border-slate-200" data-id="element-1361">
            <div className="px-8 pt-8 pb-4 border-b border-slate-100" data-id="element-1362">
              <div className="flex items-center justify-between mb-2" data-id="element-1363">
                <h2 className="text-xl font-bold text-slate-800" data-id="element-1364">
                  {step === 1 && 'Who is planning?'}
                  {step === 2 && 'Where are you based?'}
                  {step === 3 && 'Add your first asset'}
                </h2>
                <span className="text-sm font-medium text-slate-400" data-id="element-1365">
                  Step {step} of 3
                </span>
              </div>
              <div className="flex gap-2 mt-4" data-id="element-1366">
                {[1, 2, 3].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-slate-100'}`} data-id="element-1367" />)}
              </div>
            </div>

            <CardContent className="p-8" data-id="element-1368">
              {step === 1 && <div className="space-y-6" data-id="element-1369">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1370">
                    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1371">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2" data-id="element-1372">
                        <User className="w-4 h-4 text-blue-500" data-id="element-1373" /> Partner 1
                      </h3>
                      <div data-id="element-1374">
                        <Label data-id="element-1375">First Name</Label>
                        <Input placeholder="e.g. Alex" value={p1Name} onChange={e => setP1Name(e.target.value)} autoFocus data-id="element-1376" />
                      </div>
                      <div data-id="element-1377">
                        <Label data-id="element-1378">Date of Birth</Label>
                        <Input type="date" value={p1Dob} onChange={e => setP1Dob(e.target.value)} data-id="element-1379" />
                      </div>
                    </div>

                    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1380">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2" data-id="element-1381">
                        <User className="w-4 h-4 text-indigo-500" data-id="element-1382" /> Partner 2
                      </h3>
                      <div data-id="element-1383">
                        <Label data-id="element-1384">First Name</Label>
                        <Input placeholder="e.g. Jordan" value={p2Name} onChange={e => setP2Name(e.target.value)} data-id="element-1385" />
                      </div>
                      <div data-id="element-1386">
                        <Label data-id="element-1387">Date of Birth</Label>
                        <Input type="date" value={p2Dob} onChange={e => setP2Dob(e.target.value)} data-id="element-1388" />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4" data-id="element-1389">
                    <Button variant="ghost" onClick={() => setStep(0)} data-id="element-1390">
                      Cancel
                    </Button>
                    <Button variant="primary" disabled={!isStep1Valid} onClick={() => setStep(2)} data-id="element-1391">
                      Continue <ArrowRight className="w-4 h-4 ml-2" data-id="element-1392" />
                    </Button>
                  </div>
                </div>}

              {step === 2 && <div className="space-y-6" data-id="element-1393">
                  <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 space-y-6" data-id="element-1394">
                    <div data-id="element-1395">
                      <Label className="flex items-center gap-2 mb-2" data-id="element-1396">
                        <Globe className="w-4 h-4 text-slate-500" data-id="element-1397" /> Primary
                        Country
                      </Label>
                      <Select searchable value={country} onChange={e => handleCountryChange(e.target.value)} options={COUNTRIES.map(c => ({
                  value: c.code,
                  label: `${c.flag} ${c.name}`
                }))} data-id="element-1398" />
                      <p className="text-xs text-slate-500 mt-2" data-id="element-1399">
                        This helps us set sensible defaults for your assets.
                      </p>
                    </div>

                    <div data-id="element-1400">
                      <Label className="flex items-center gap-2 mb-2" data-id="element-1401">
                        <Wallet className="w-4 h-4 text-slate-500" data-id="element-1402" /> Base
                        Currency
                      </Label>
                      <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
                  value: c.code,
                  label: `${c.flag} ${c.code} (${c.symbol})`
                }))} data-id="element-1403" />
                      <p className="text-xs text-slate-500 mt-2" data-id="element-1404">
                        All your projections and charts will be displayed in
                        this currency.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4" data-id="element-1405">
                    <Button variant="ghost" onClick={() => setStep(1)} data-id="element-1406">
                      Back
                    </Button>
                    <Button variant="primary" onClick={() => setStep(3)} data-id="element-1407">
                      Continue <ArrowRight className="w-4 h-4 ml-2" data-id="element-1408" />
                    </Button>
                  </div>
                </div>}

              {step === 3 && <div className="space-y-6" data-id="element-1409">
                  <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 space-y-5" data-id="element-1410">
                    <p className="text-sm text-slate-600 mb-2" data-id="element-1411">
                      Let's add your first asset to get the dashboard started.
                      You can add more later.
                    </p>

                    <div data-id="element-1412">
                      <Label data-id="element-1413">Asset Name</Label>
                      <Input placeholder="e.g. Main Checking Account" value={assetName} onChange={e => setAssetName(e.target.value)} autoFocus data-id="element-1414" />
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1415">
                      <div data-id="element-1416">
                        <Label data-id="element-1417">Amount ({currency})</Label>
                        <Input type="number" min="0" placeholder="0.00" value={assetAmount} onChange={e => setAssetAmount(e.target.value)} data-id="element-1418" />
                      </div>
                      <div data-id="element-1419">
                        <Label data-id="element-1420">Asset Type</Label>
                        <Select value={assetType} onChange={e => setAssetType(e.target.value as HoldingType)} options={[{
                    value: 'Liquid',
                    label: '💧 Liquid Cash/Stocks'
                  }, {
                    value: 'Illiquid',
                    label: '🏢 Illiquid Asset'
                  }]} data-id="element-1421" />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4" data-id="element-1422">
                    <Button variant="ghost" onClick={() => setStep(2)} data-id="element-1423">
                      Back
                    </Button>
                    <Button variant="primary" disabled={!isStep3Valid} onClick={handleFinishManual} data-id="element-1424">
                      <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-1425" /> Complete Setup
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </Card>}
      </div>
    </div>;
}